import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Modal, Button, Form, Alert, Badge, Spinner, Row, Col } from 'react-bootstrap';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import { actualizarDatos, postDataItem, getItemsActivos, getItemsContable, getDataList, buscarItemsPorNombre } from '../../api/common';
import { Alertassw } from '../Alertas/Alertassw';
import { SelectMultPatron } from './SelectMultPatron';
import SelectMg from './SelectMg';
import { PatronesAuxilires } from './PatronesAuxiliares';

const EMPTY_TAX_ROW = { id: '', porcentaje: 0, name: '', idContable: '' };

const normalizeTaxRows = (taxRows) => {
    const rows = Array.isArray(taxRows) ? taxRows : taxRows ? [taxRows] : [];
    const normalized = rows
        .filter(Boolean)
        .map((tax) => ({
            id: tax.id || '',
            porcentaje: Number(tax.porcentaje ?? tax.percentage ?? 0) || 0,
            name: tax.name || '',
            idContable: tax.idContable || ''
        }));

    return normalized.length > 0 ? normalized : [{ ...EMPTY_TAX_ROW }];
};

const hasMeaningfulTaxRows = (taxRows) =>
    Array.isArray(taxRows) && taxRows.some((tax) => {
        if (!tax) return false;
        return Boolean(tax.id || tax.name || Number(tax.porcentaje ?? tax.percentage ?? 0) > 0);
    });

export const EditarItem = ({ show, onHide, item, hayProveedor, onSaved }) => {
    const esEditar = item !== null;

    const [form, setForm] = useState({
        nombre: '',
        alias: '',
        grupo: '',
        tiempo: 45,
        cantidad: '',
        precio: 0,
        numPatrones: 1,
        descripcion: '',
        magnitud: '',
        itemsAdicionales: [],
        tax: [{ ...EMPTY_TAX_ROW }]
    });

    const [patronesUtilizados, setPatronesUtilizados] = useState([[]]);
    const [patronesAuxiliares, setPatronesAuxiliares] = useState([]);
    const [selectedContable, setSelectedContable] = useState(null);
    const [providerName, setProviderName] = useState('');
    const [taxes, setTaxes] = useState([]);
    const [sinFieldMapping, setSinFieldMapping] = useState(false);
    const [contableDebug, setContableDebug] = useState(null);
    const selectContableRef = useRef(null);
    const [itemsActivos, setItemsActivos] = useState([]);
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const [startPage, setStartPage] = useState(0);
    const [contableOptions, setContableOptions] = useState([]);
    const [loadingContable, setLoadingContable] = useState(false);
    const [contableInputValue, setContableInputValue] = useState('');
    // Modal informativo sobre "Ítems adicionales" (items dependientes).
    const [showInfoItems, setShowInfoItems] = useState(false);
    // Opciones ya seleccionadas del AsyncSelect (preserva nombres aunque no
    // estén en la respuesta actual de la búsqueda server-side).
    const [itemsAdSel, setItemsAdSel] = useState([]);

    // Inicializar form cuando cambia el item
    useEffect(() => {
        if (!show) return;
        setErrors({});
        if (esEditar && item) {
            setForm({
                nombre: item.nombre || '',
                alias: item.alias || '',
                grupo: item.grupo || '',
                tiempo: item.tiempo ?? 45,
                cantidad: item.cantidad ?? '',
                precio: item.precio ?? 0,
                numPatrones: item.numPatrones ?? 1,
                descripcion: item.descripcion || '',
                magnitud: item.magnitud?._id || item.magnitud || '',
                itemsAdicionales: (item.itemsAdicionales || []).map(i => i._id || i),
                tax: normalizeTaxRows(item.tax || item.contabilidad?.tax)
            });
            setPatronesUtilizados(
                item.patronesUtilizados?.length > 0 ? item.patronesUtilizados : [[]]
            );
            setPatronesAuxiliares(item.patronesAuxiliares || []);
            setSelectedContable(item.contabilidad?.id ? item.contabilidad : null);
        } else {
            setForm({ nombre: '', alias: '', grupo: '', tiempo: 45, cantidad: '', precio: 0, numPatrones: 1, descripcion: '', magnitud: '', itemsAdicionales: [], tax: [{ ...EMPTY_TAX_ROW }] });
            setPatronesUtilizados([[]]);
            setPatronesAuxiliares([]);
            setSelectedContable(null);
        }
        // Reset select contable al abrir/cerrar modal
        if (selectContableRef.current) selectContableRef.current.clearValue();
        setContableOptions([]);
        setContableInputValue('');
    }, [show, item]);

    // Cargar items activos para "Items adicionales" (lista general — se mantiene
    // como fallback; el AsyncSelect busca directamente en BD).
    useEffect(() => {
        if (!show) return;
        getItemsActivos().then(res => setItemsActivos(res?.data?.data?.items || [])).catch(() => {});
    }, [show]);

    // Hidrata las opciones ya seleccionadas de "Ítems adicionales" con sus
    // nombres y magnitudes. En producción `item.itemsAdicionales` suele venir
    // como array de ObjectIds (sin populate), por eso hacemos un lookup
    // explícito para que las etiquetas se muestren correctamente aunque el
    // item seleccionado no aparezca en la primera página de la búsqueda.
    useEffect(() => {
        if (!show) { setItemsAdSel([]); return; }
        const ids = (item?.itemsAdicionales || [])
            .map(i => (i && typeof i === 'object' ? i._id : i))
            .filter(Boolean);
        if (ids.length === 0) { setItemsAdSel([]); return; }

        // Si vienen populados (objetos con nombre), usamos directo.
        const yaPoblados = (item.itemsAdicionales || [])
            .filter(i => i && typeof i === 'object' && i.nombre)
            .map(i => ({
                value: i._id,
                label: i.nombre + (i.magnitud?.consecutivo ? ` — ${i.magnitud.consecutivo}` : ''),
            }));
        if (yaPoblados.length === ids.length) {
            setItemsAdSel(yaPoblados);
            return;
        }

        // En caso contrario, lookup por `_id` en Items.
        getDataList('Items', { conditions: { _id: { $in: ids } } })
            .then(res => {
                const docs = res?.data?.data || [];
                setItemsAdSel(docs.map(it => ({
                    value: it._id,
                    label: it.nombre + (it.magnitud?.consecutivo ? ` — ${it.magnitud.consecutivo}` : ''),
                })));
            })
            .catch(() => setItemsAdSel(yaPoblados));
    }, [show, item]);

    // Cargar impuestos disponibles para mostrar en el select de impuestos
    useEffect(() => {
        if (!show) return;
       const taxes = loadTaxesOptions().then(taxes => setTaxes(taxes)).catch(() => {});
        console.log('Impuestos cargados para opciones:', taxes);
    }, [show]);

    // Verificar FieldMapping y nombre del proveedor al abrir
    useEffect(() => {
        if (!show || !hayProveedor || selectedContable) return;
        setSinFieldMapping(false);
        setContableDebug(null);
        getItemsContable(0, 1).then(res => {
            if (res?.data?.data?.hayProveedor) {
                if (res.data.data.sinFieldMapping) {
                    setSinFieldMapping(true);
                } else {
                    setContableDebug(res.data.data.debug || null);
                }
                setProviderName(res.data.data.providerName || '');
            }
        }).catch(() => {});
    }, [show, hayProveedor, selectedContable]);

    const loadContableOptions = useCallback(async (inputValue, start=0) => {

        console.log('Buscando opciones contables con input:', start);
        try {
            const res = await getItemsContable(start, 30, inputValue);
            const data = res?.data?.data;
            if (!data?.hayProveedor || data.sinFieldMapping) return [];
            setStartPage(start);
            return (data.items || []).map((ic, index) => ({
                value: ic.id,
                label: start+index + 1 + '. ' + ic.name + (ic.reference ? ` (${ic.reference})` : ''),
                contable: ic,
            }));
        } catch {
            return [];
        }
    }, []);

    const handleSearchContable = useCallback(async (inputValue) => {
        setContableInputValue(inputValue);
        setLoadingContable(true);

        const opts = await loadContableOptions(inputValue, 0);
        setContableOptions(opts);
        setLoadingContable(false);
    }, [loadContableOptions]);

    const handleLoadMore = useCallback(async () => {
        setLoadingContable(true);
        const newOpts = await loadContableOptions(contableInputValue, startPage + 30);
        setContableOptions(prev => [...prev, ...newOpts]);
        setLoadingContable(false);
    }, [loadContableOptions, contableInputValue, startPage]);

    //funcion para cargar las opciones de impuesto desde la base de datos
    const loadTaxesOptions = useCallback(async () => {
        try {
            const res = await getDataList('taxes' );
            const data = res?.data?.data;
            console.log('Data recibida para impuestos:', data);

            setTaxes(Array.isArray(data) ? data : []);
            return Array.isArray(data) ? data : [];

        } catch {
            return [];
        }    }, []);

    // Pre-cargar opciones contables al abrir el modal
    useEffect(() => {
        if (!show || !hayProveedor || sinFieldMapping || selectedContable) return;
        handleSearchContable('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [show, hayProveedor, sinFieldMapping, selectedContable]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: null }));
    };

    const handleMagnitud = (e) => {
        setForm(prev => ({ ...prev, magnitud: e.target.value }));
        if (errors.magnitud) setErrors(prev => ({ ...prev, magnitud: null }));
    };

    const handlePatrones = (index, e) => {
        const updated = [...patronesUtilizados];
        updated[index] = e.target.value;
        setPatronesUtilizados(updated);
    };

    const handleTaxChange = (index, field, value) => {
        setForm(prev => ({
            ...prev,
            tax: prev.tax.map((tax, taxIndex) => (
                taxIndex === index
                    ? { ...tax, [field]: field === 'porcentaje' ? Number(value) || 0 : value }
                    : tax
            ))
        }));
    };

    const handleAddTaxRow = () => {
        setForm(prev => ({
            ...prev,
            tax: [...normalizeTaxRows(prev.tax), { ...EMPTY_TAX_ROW }]
        }));
    };

    const handleRemoveTaxRow = (index) => {
        setForm(prev => {
            const nextTax = prev.tax.filter((_, taxIndex) => taxIndex !== index);
            return {
                ...prev,
                tax: nextTax.length > 0 ? nextTax : [{ ...EMPTY_TAX_ROW }]
            };
        });
    };

    const getSelectedPatronIds = (excludeIndex) =>
        patronesUtilizados.filter((_, i) => i !== excludeIndex).flat(2);

    const validate = () => {
        const errs = {};
        if (!form.nombre.trim()) errs.nombre = 'El nombre es requerido';
        if (!form.magnitud) errs.magnitud = 'La magnitud es requerida';
        if (form.precio < 0) errs.precio = 'El precio no puede ser negativo';
        if (!form.tiempo || form.tiempo <= 0) errs.tiempo = 'El tiempo debe ser mayor a 0';
        return errs;
    };

    const handleGuardar = async () => {
        const errs = validate();
        if (Object.keys(errs).length > 0) {
            setErrors(errs);
            return;
        }
        setSaving(true);
        try {
            const body = {
                ...form,
                nombre: form.nombre.trim(),
                alias: form.alias?.trim() || form.nombre.trim(),
                tax: normalizeTaxRows(form.tax),
                patronesUtilizados,
                patronesAuxiliares,
                ...(selectedContable && { contabilidad: selectedContable }),
            };
            if (esEditar) {
                await actualizarDatos('Items', body, item._id);
            } else {
                await postDataItem(body);
            }
            Alertassw('charge', esEditar ? 'Ítem actualizado' : 'Ítem creado');
            onSaved?.();
            onHide();
        } catch (err) {
            const msg = err.response?.data?.detail || err.response?.data?.error?.message || err.message;
            if (err.response?.status === 409) {
                setErrors({ nombre: msg });
            } else {
                Alertassw('error', 'Error al guardar', msg);
            }
        } finally {
            setSaving(false);
        }
    };

    const handletaxChange = (index, field, value) => {
        setForm(prev => ({
            ...prev,
                tax: prev.tax.map((tax, taxIndex) => (
                taxIndex === index
                    ? { ...tax, [field]: field === 'porcentaje' ? Number(value) || 0 : value }
                    : tax
            ))
        }));
    };

    // AsyncSelect de "Ítems adicionales": busca en BD con cada tecla (regex
    // server-side sobre `nombre`). Antes se filtraba sobre `itemsActivos`
    // (solo los primeros 20 cargados), lo que ocultaba items en producción
    // con catálogo grande.
    const mapItemOption = (it) => ({
        value: it._id,
        label: it.nombre + (it.magnitud?.consecutivo ? ` — ${it.magnitud.consecutivo}` : ''),
    });

    const loadOptionsItemsAd = useCallback(async (inputValue) => {
        const term = (inputValue || '').trim();
        try {
            // Sin término: traer primeros 50 ordenados A→Z para no mostrar
            // menú vacío. Con término: regex server-side sobre `nombre`.
            const res = term
                ? await buscarItemsPorNombre(term, 50)
                : await getItemsActivos({ limit: 50 });
            const items = res?.data?.data?.items || [];
            return items
                .filter(it => !esEditar || it._id !== item?._id)
                .map(mapItemOption);
        } catch {
            return [];
        }
    }, [esEditar, item]);


    return (
        <>
        <Modal show={show} onHide={onHide} size="lg" centered scrollable dialogClassName="modal-80vh">
            <Modal.Header closeButton>
                <Modal.Title>{esEditar ? `Editar ítem: ${item?.nombre}` : 'Nuevo ítem'}</Modal.Title>
            </Modal.Header>
            <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                {/* Vinculación contable */}
            <Row className="mb-3">
                <Col md={10}>
                <Form.Group className="mb-3">
                    <Form.Label className="fw-semibold">
                        Vinculación contable {hayProveedor && providerName ? `— ${providerName}` : ''}
                    </Form.Label>
                    {!hayProveedor ? (
                        <Alert variant="secondary" className="py-2">
                            🔒 Sin proveedor contable activo — configure uno en Configuración Contable
                        </Alert>
                    ) : sinFieldMapping ? (
                        <Alert variant="warning" className="py-2">
                            ⚠️ No existen FieldMappings para document_type <strong>'item'</strong>.
                            Configure los mapeos de campos en <strong>Configuración Contable</strong> antes de vincular productos.
                        </Alert>
                    ) : selectedContable ? (
                        <div className="d-flex align-items-center gap-2">
                            {/*Se debe limitar el largo del nombre y un tooltip*/}
                            <Badge  bg="success" title={selectedContable.name}>✅ {selectedContable.name.length > 30 ? `${selectedContable.name.substring(0, 30)}...` : selectedContable.name} (ID: {selectedContable.id})</Badge>
                            <Button size="sm" variant="link" onClick={() => setSelectedContable(null)}>
                                Cambiar vinculación
                            </Button>
                        </div>
                    ) : (
                        <Select
                            ref={selectContableRef}
                            options={contableOptions}
                            isLoading={loadingContable}
                            onInputChange={(inputValue, { action }) => {
                                if (action !== 'input-change') return;
                                handleSearchContable(inputValue);
                            }}
                            filterOption={() => true}

                            onChange={option => {
                                const ic = option?.contable; 

                                console.log('ic completo:', ic);           // ¿tiene autoFill?
                                console.log('autoFill:', ic?.autoFill);    // ¿tiene .tax?


                                if (!ic) { setSelectedContable(null); return; }
                                const { autoFill, ...contable } = ic;
                                setSelectedContable(contable);

                                // Tax puede venir en autoFill.tax o directamente en ic.taxes / ic.tax
                                const rawTax = autoFill?.tax ?? ic.taxes ?? ic.tax ?? null;
                               
                                if (autoFill || rawTax) {

                                    // Normalizar el tax del externo: su "id" es el idContable para nosotros
                                    const taxNormalizado = rawTax
                                        ? (Array.isArray(rawTax) ? rawTax : [rawTax]).map(t => ({
                                            id: t.id || '',
                                            idContable: t.idContable || t.id || '',
                                            name: t.name || '',
                                            porcentaje: Number(t.porcentaje ?? t.percentage ?? 0) || 0,
                                        }))
                                        : null;

                                    setForm(prev => ({
                                        ...prev,
                                        ...(autoFill.nombre !== undefined && (!esEditar || !prev.nombre)
                                            ? { nombre: autoFill.nombre } : {}),
                                        // alias = mismo nombre del proveedor si el campo está vacío
                                        ...(autoFill.nombre !== undefined && (!esEditar || !prev.alias)
                                            ? { alias: autoFill.nombre } : {}),
                                        ...(autoFill.descripcion !== undefined && (!esEditar || !prev.descripcion)
                                            ? { descripcion: autoFill.descripcion } : {}),
                                        ...(autoFill.precio !== undefined && autoFill.precio !== null && (!esEditar || !prev.precio)
                                            ? { precio: Number(autoFill.precio) || 0 } : {}),
                                        ...(taxNormalizado && (!esEditar || !hasMeaningfulTaxRows(prev.tax))
                                            ? { tax: taxNormalizado } : {})
                                    }));
                                }
                            }}

                            isClearable
                            placeholder="Buscar producto contable..."
                            noOptionsMessage={() => 'Sin resultados'}
                            loadingMessage={() => 'Cargando...'}
                        />
                    )}
                </Form.Group>
                </Col>
                {/*Boton para cargar mas items  30*/}
                <Col md={2} className="d-flex align-items-end justify-content-end">
                    <div className="mb-3" >
                        {/**Lo muestro siempre si no hay contable seleccionado */}
                        {!selectedContable && (
                        <Button variant="outline-secondary" size="sm" onClick={handleLoadMore} disabled={loadingContable}>
                            {loadingContable
                                ? <Spinner animation="border" size="sm" />
                                : <><i className="bi bi-arrow-clockwise"></i> Cargar +</>
                            }
                        </Button>
                        )}
                    </div>
                </Col>
                </Row>

                <hr />
                
                {/* Información básica */}
                <Form.Group className="mb-3">
                    <Form.Label>Nombre <span className="text-danger">*</span></Form.Label>
                    <Form.Control
                        name="nombre"
                        value={form.nombre}
                        onChange={handleChange}
                        isInvalid={!!errors.nombre}
                    />
                    <Form.Control.Feedback type="invalid">{errors.nombre}</Form.Control.Feedback>
                </Form.Group>

                {/* Input alias nombre alternativo por defecto igual al nombre pero se puede usar para registrar un alias que se use en el proveedor contable para que al vincularlo se autocompleten los datos del ítem */}
                <Form.Group className="mb-3">
                    <Form.Label>Alias</Form.Label>
                    <Form.Control
                        name="alias"
                        value={form.alias}
                        onChange={handleChange}
                        isInvalid={!!errors.alias}
                    />
                    <Form.Control.Feedback type="invalid">{errors.alias}</Form.Control.Feedback>
                </Form.Group>

                <Form.Group className="mb-3">
                    <Form.Label>Magnitud <span className="text-danger">*</span></Form.Label>
                    <SelectMg handleData={handleMagnitud} dataDefault={esEditar ? item?.magnitud : null} />
                    {errors.magnitud && <div className="text-danger small mt-1">{errors.magnitud}</div>}
                </Form.Group>

                <div className="row g-3 mb-3">
                    <div className="col-md-4">
                        <Form.Group>
                            <Form.Label>Tiempo (min) <span className="text-danger">*</span></Form.Label>
                            <Form.Control
                                name="tiempo"
                                type="number"
                                value={form.tiempo}
                                onChange={handleChange}
                                isInvalid={!!errors.tiempo}
                            />
                            <Form.Control.Feedback type="invalid">{errors.tiempo}</Form.Control.Feedback>
                        </Form.Group>
                    </div>
                    <div className="col-md-4">
                        <Form.Group>
                            <Form.Label>Cantidad/día</Form.Label>
                            <Form.Control name="cantidad" type="number" value={form.cantidad} onChange={handleChange} />
                        </Form.Group>
                    </div>
                    <div className="col-md-4">
                        <Form.Group>
                            <Form.Label>Precio</Form.Label>
                            <Form.Control
                                name="precio"
                                type="number"
                                value={form.precio}
                                onChange={handleChange}
                                isInvalid={!!errors.precio}
                            />
                            <Form.Control.Feedback type="invalid">{errors.precio}</Form.Control.Feedback>
                        </Form.Group>
                    </div>
                </div>

                <div className="row g-3 mb-3">
                    <div className="col-md-6">
                        <Form.Group>
                            <Form.Label>Núm. patrones</Form.Label>
                            <Form.Control name="numPatrones" type="number" value={form.numPatrones} onChange={handleChange} />
                        </Form.Group>
                    </div>
                    <div className="col-md-6">
                        <Form.Group>
                            <Form.Label>Grupo</Form.Label>
                            <Form.Control name="grupo" value={form.grupo} onChange={handleChange} />
                        </Form.Group>
                    </div>
                </div>

                <Form.Group className="mb-3">
                    <Form.Label>Descripción</Form.Label>
                    <Form.Control as="textarea" name="descripcion" rows={2} value={form.descripcion} onChange={handleChange} />
                </Form.Group>

                <Form.Group className="mb-3">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                        <Form.Label className="mb-0">Impuestos</Form.Label>
                        <Button variant="outline-secondary" size="sm" onClick={handleAddTaxRow}>
                            Agregar impuesto
                        </Button>
                    </div>
                    {/** aqui va un select que trae los taxes del estado taxes creado con form.select*/}
                    <div className="mb-2">
                        <Form.Label className="small text-muted">Seleccionar impuesto para agregar</Form.Label>
                        <Select
                            options={taxes.map(tax => ({    
                                value: tax._id,
                                label: `${tax.name} (${tax.porcentaje}%)`,
                                tax,    
                            }))}
                            onChange={(option) => {
                                if (!option?.tax) return;
                                const taxToAdd = {
                                    id: option.tax?.idContable,
                                    name: option.tax?.name,
                                    porcentaje: option.tax?.porcentaje,
                                    idContable: option.tax?.idContable,
                                };
                                // solo permite un impuesto en el array, si ya hay uno, lo reemplaza por el nuevo seleccionado
                                setForm(prev => ({
                                    ...prev,
                                    tax: [taxToAdd]
                                }));
                            }}
                            placeholder="Buscar impuesto..."
                            noOptionsMessage={() => 'Sin impuestos disponibles'}
                        />
                       
                    </div>
                    {normalizeTaxRows(form.tax).map((tax, index) => (
                        <div key={`${tax.id || 'tax'}-${index}`} className="row g-2 align-items-end mb-2">
                            <div className="col-md-3">
                                <Form.Label className="small text-muted">ID Software Contable</Form.Label>
                                <Form.Control
                                    value={tax.idContable || tax.id || 'No hay ID contable '}
                                    onChange={(e) => handleTaxChange(index, 'idContable', e.target.value)}
                                    placeholder= "ID en software contable"
                                    disabled

                                />
                            </div>
                            <div className="col-md-5">
                                <Form.Label className="small text-muted">Nombre</Form.Label>
                                <Form.Control
                                    value={tax.name}
                                    onChange={(e) => handleTaxChange(index, 'name', e.target.value)}
                                    placeholder="Nombre impuesto"
                                />
                            </div>
                            <div className="col-md-3">
                                <Form.Label className="small text-muted">Porcentaje</Form.Label>
                                <Form.Control
                                    type="number"
                                    value={tax.porcentaje}
                                    onChange={(e) => handleTaxChange(index, 'porcentaje', e.target.value)}
                                    placeholder="0"
                                />
                            </div>
                            <div className="col-md-1 d-grid">
                                <Button
                                    variant="outline-danger"
                                    onClick={() => handleRemoveTaxRow(index)}
                                    disabled={normalizeTaxRows(form.tax).length === 1 && !hasMeaningfulTaxRows(form.tax)}
                                >
                                    <i className="bi bi-trash"></i>
                                </Button>
                            </div>
                        </div>
                    ))}
                </Form.Group>

                <hr />
                {/* Patrones */}
                <div className="mb-3">
                    <strong>Patrones Autorizados</strong>
                    {patronesUtilizados.map((subArray, index) => (
                        <div key={index} className="mb-2">
                            <Form.Label>Grupo {index + 1}</Form.Label>
                            <SelectMultPatron
                                dataDefault={subArray}
                                handledata={(e) => handlePatrones(index, e)}
                                name={`patronesUtilizados_${index}`}
                                collection="Patrones"
                                field="item"
                                excludeIds={getSelectedPatronIds(index)}
                            />
                        </div>
                    ))}
                    <span
                        className="btn btn-sm btn-outline-secondary mt-1"
                        onClick={() => setPatronesUtilizados(p => [...p, []])}
                    >
                        + Agregar grupo
                    </span>
                </div>

                <Form.Group className="mb-3">
                    <Form.Label>Patrones Auxiliares</Form.Label>
                    <PatronesAuxilires
                        dataDefault={patronesAuxiliares}
                        handledata={(e) => {
                            const val = e.target.value;
                            setPatronesAuxiliares(val);
                        }}
                        name="patronesAuxiliares"
                        collection="Patrones"
                        field="item"
                        baseFilter={{ tipoEquipo: 'Auxiliar' }}
                    />
                </Form.Group>

                {/* Ítems adicionales — searchable server-side */}
                <Form.Group className="mb-3">
                    <Form.Label className="d-flex align-items-center gap-2 mb-1">
                        <span>Ítems adicionales</span>
                        {/* Icono informativo: abre modal explicando qué son los
                            items dependientes y para qué se usan. */}
                        <button
                            type="button"
                            className="btn btn-link p-0 lh-1 text-primary"
                            onClick={() => setShowInfoItems(true)}
                            title="¿Qué son los ítems adicionales?"
                            aria-label="Información sobre ítems adicionales"
                        >
                            <i className="bi bi-info-circle-fill" style={{ fontSize: '1rem' }} />
                        </button>
                    </Form.Label>
                    <AsyncSelect
                        isMulti
                        cacheOptions
                        defaultOptions
                        loadOptions={loadOptionsItemsAd}
                        value={itemsAdSel}
                        onChange={(selected) => {
                            const list = selected || [];
                            setItemsAdSel(list);
                            setForm(p => ({ ...p, itemsAdicionales: list.map(o => o.value) }));
                        }}
                        placeholder="Escribe para buscar ítems en la base de datos..."
                        noOptionsMessage={({ inputValue }) =>
                            inputValue ? 'Sin resultados' : 'Escribe para buscar...'
                        }
                        loadingMessage={() => 'Buscando...'}
                    />
                    <Form.Text className="text-muted">
                        La búsqueda se hace directamente contra la base de datos — no está limitada a la primera página.
                    </Form.Text>
                </Form.Group>

            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide} disabled={saving}>Cancelar</Button>
                <Button variant="primary" onClick={handleGuardar} disabled={saving}>
                    {saving ? <><Spinner animation="border" size="sm" className="me-1" />Guardando...</> : 'Guardar'}
                </Button>
            </Modal.Footer>
        </Modal>

        {/* Modal informativo: qué son los ítems adicionales (dependientes). */}
        <Modal show={showInfoItems} onHide={() => setShowInfoItems(false)} centered>
            <Modal.Header closeButton>
                <Modal.Title>
                    <i className="bi bi-info-circle-fill text-primary me-2" />
                    Ítems adicionales (dependientes)
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p>
                    Los <strong>ítems adicionales</strong> son otros ítems que se agregan
                    automáticamente a una Orden de Trabajo cada vez que se selecciona
                    el ítem actual.
                </p>
                <p className="mb-2"><strong>¿Cuándo usarlos?</strong></p>
                <ul>
                    <li>
                        Cuando un equipo requiere, además de este ítem, otros servicios o
                        procedimientos que <em>siempre</em> deben realizarse en conjunto.
                    </li>
                    <li>
                        Para evitar que el usuario tenga que seleccionar manualmente cada
                        ítem relacionado al crear la OT.
                    </li>
                </ul>
                <p className="mb-2"><strong>Ejemplo:</strong></p>
                <Alert variant="light" className="mb-3 border">
                    Al seleccionar <em>“Calibración de balanza analítica”</em>, el sistema
                    agrega automáticamente <em>“Verificación de pesas patrón”</em> y
                    <em> “Limpieza del plato”</em>.
                </Alert>
                <p className="mb-0 small text-muted">
                    La búsqueda se realiza directamente contra la base de datos, así que
                    puedes escribir cualquier palabra del nombre del ítem (no está limitada
                    a los primeros resultados cargados).
                </p>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="primary" onClick={() => setShowInfoItems(false)}>
                    Entendido
                </Button>
            </Modal.Footer>
        </Modal>
        </>
    );
};
